import { createMuiTheme } from '@material-ui/core/styles';
import {
  T,
  DAY,
  INDICATOR,
  INDICATOR_TEXT,
  FONT_SANS,
  FONT_MONO,
  PILL_SHADOW
} from './constants/tokens';

/**
 * The v7 theme.
 *
 * `overrides` and `typography` use `var(--sa-*)` expressions, so the night
 * theme swaps by flipping `data-theme` on <html> without a re-render — see
 * src/index.css. MUI only writes those strings into CSS, never reads them.
 *
 * `palette` cannot: MUI v4 runs it through `augmentColor` and
 * `getContrastRatio`, which parse the value and throw on a `var()`. So the
 * palette carries the day hexes (`DAY`), and anything that must follow the
 * night theme is themed via `overrides` rather than read off `palette`.
 */
export default createMuiTheme({
  palette: {
    primary: {
      main: DAY.ink,
      dark: DAY.inkActive,
      contrastText: DAY.canvas
    },
    secondary: {
      main: INDICATOR.accent,
      contrastText: '#ffffff'
    },
    error: {
      main: INDICATOR.error,
      contrastText: '#ffffff'
    },
    warning: {
      main: INDICATOR.warning,
      contrastText: '#1a1c16'
    },
    success: {
      main: INDICATOR.success,
      contrastText: '#ffffff'
    },
    info: {
      main: INDICATOR.accent,
      contrastText: '#ffffff'
    },
    background: {
      default: DAY.canvas,
      paper: DAY.surfaceCard
    },
    text: {
      primary: DAY.ink,
      secondary: DAY.body,
      disabled: DAY.mutedSoft,
      hint: DAY.muted
    },
    divider: DAY.hairline
  },

  typography: {
    fontFamily: FONT_SANS,

    // Deliberately no `color` on any variant. MUI applies these to every
    // Typography, so a colour here wins over the surface the text sits on —
    // it turned AlertTitle dark grey on a red emergency bar. Colour is the
    // component's business; palette.text supplies the defaults.

    // Page title. The design sets these three tightly; do not loosen the
    // letter-spacing, it is what makes the display sizes read as one family.
    h1: {
      fontSize: '48px',
      fontWeight: 600,
      lineHeight: '48px',
      letterSpacing: '-2.4px',
      margin: 0
    },
    h2: {
      fontSize: '32px',
      fontWeight: 600,
      lineHeight: '38px',
      letterSpacing: '-1.2px'
    },
    h3: {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: '32px',
      letterSpacing: '-0.6px'
    },
    h4: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: '28px',
      letterSpacing: '-0.4px'
    },
    h5: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '-0.32px'
    },
    h6: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      letterSpacing: '-0.28px'
    },

    body1: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px'
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px'
    },

    subtitle1: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      letterSpacing: '-0.28px'
    },
    // The recurring uppercase mono eyebrow above stats and sections.
    subtitle2: {
      fontFamily: FONT_MONO,
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '16px',
      textTransform: 'uppercase'
    },
    // Numerics, codes and timestamps. Mono keeps columns aligned.
    caption: {
      fontFamily: FONT_MONO,
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px'
    },

    button: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      letterSpacing: '-0.28px',
      textTransform: 'none'
    }
  },

  shape: {
    borderRadius: 6
  },

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 970,
      lg: 1280,
      xl: 1920
    }
  },

  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: {
          backgroundColor: T.canvas
        }
      }
    },

    MuiPaper: {
      root: {
        backgroundColor: T.surfaceCard,
        color: T.body
      },
      rounded: {
        borderRadius: 6
      },
      // The design uses hairline borders, not shadows, to separate surfaces.
      elevation1: {
        boxShadow: 'none',
        border: `1px solid ${T.hairline}`
      },
      elevation2: {
        boxShadow: PILL_SHADOW,
        border: `1px solid ${T.hairline}`
      }
    },

    MuiCard: {
      root: {
        backgroundColor: T.surfaceCard,
        border: `1px solid ${T.hairline}`,
        borderRadius: 6,
        boxShadow: 'none'
      }
    },

    MuiButton: {
      root: {
        height: 40,
        padding: '0 20px',
        borderRadius: 6,
        textTransform: 'none',
        fontWeight: 500
      },
      // No background or colour here. An outlined button has to work on a
      // coloured surface as well as on the canvas: forcing white made the
      // Redial control on the red emergency-dispatch bar white-on-white.
      // MUI's transparent default reads correctly on both.
      outlined: {
        border: `1px solid ${T.hairline}`,
        padding: '0 20px'
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none'
        }
      },
      // The school's accent; the ink without one.
      containedPrimary: {
        backgroundColor: T.accent,
        color: T.onAccent,
        '&:hover': {
          backgroundColor: T.accentHover
        }
      },
      text: {
        padding: '0 12px'
      }
    },

    // Shape only. Like the outlined button, an icon button has to survive
    // landing on a coloured surface — forcing an elevated background made the
    // dismiss control on the red emergency bar white-on-white. Screens that
    // want the filled look set it themselves.
    MuiIconButton: {
      root: {
        width: 36,
        height: 36,
        padding: 0,
        border: `1px solid ${T.hairline}`,
        borderRadius: 9999
      }
    },

    MuiFab: {
      root: {
        boxShadow: PILL_SHADOW,
        backgroundColor: T.elevated,
        color: T.ink,
        border: `1px solid ${T.hairline}`,
        '&:hover': {
          backgroundColor: T.surfaceSoft
        }
      }
    },

    MuiChip: {
      root: {
        borderRadius: 9999,
        backgroundColor: T.surfaceSoft,
        color: T.ink,
        fontSize: 12,
        fontWeight: 500,
        height: 24
      }
    },

    MuiTabs: {
      root: {
        minHeight: 40
      },
      indicator: {
        backgroundColor: T.accent,
        height: 2
      }
    },

    MuiTab: {
      root: {
        minHeight: 40,
        textTransform: 'none',
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: '-0.28px',
        color: T.body,
        '&$selected': {
          color: T.ink
        }
      }
    },

    MuiTable: {
      root: {
        backgroundColor: T.surfaceCard
      }
    },

    MuiTableCell: {
      root: {
        borderBottom: `1px solid ${T.hairlineSoft}`,
        fontSize: 14,
        color: T.body,
        padding: '0 16px'
      },
      head: {
        fontFamily: FONT_MONO,
        fontSize: 12,
        fontWeight: 500,
        textTransform: 'uppercase',
        color: T.muted,
        borderBottom: `1px solid ${T.hairline}`
      }
    },

    // No height here: Table sets it from ROW_HEIGHT so dense and comfortable
    // are both reachable. A fixed value in the theme would win over both.
    MuiTableRow: {
      root: {
        '&$hover:hover': {
          backgroundColor: T.surfaceSoft
        }
      }
    },

    MuiInputBase: {
      root: {
        fontSize: 14,
        color: T.ink
      }
    },

    MuiOutlinedInput: {
      root: {
        borderRadius: 6,
        backgroundColor: T.elevated
      },
      notchedOutline: {
        borderColor: T.hairline
      }
    },

    MuiFormLabel: {
      root: {
        fontSize: 14,
        color: T.muted,
        '&$focused': {
          color: T.ink
        }
      }
    },

    MuiAppBar: {
      root: {
        boxShadow: 'none'
      },
      colorDefault: {
        backgroundColor: T.canvas,
        color: T.ink,
        borderBottom: `1px solid ${T.hairline}`
      }
    },

    MuiToolbar: {
      regular: {
        height: 64,
        minHeight: 64
      }
    },

    MuiDrawer: {
      paper: {
        backgroundColor: T.rail,
        color: T.railInk,
        borderRight: `1px solid ${T.railHairline}`
      }
    },

    MuiDialog: {
      paper: {
        backgroundColor: T.elevated,
        border: `1px solid ${T.hairline}`,
        borderRadius: 6,
        boxShadow: 'none'
      }
    },

    MuiBackdrop: {
      root: {
        backgroundColor: T.scrim
      }
    },

    MuiPopover: {
      paper: {
        backgroundColor: T.elevated,
        border: `1px solid ${T.hairline}`,
        borderRadius: 6,
        boxShadow: PILL_SHADOW
      }
    },

    MuiDivider: {
      root: {
        backgroundColor: T.hairlineSoft
      }
    },

    MuiLinearProgress: {
      root: {
        height: 6,
        borderRadius: 9999,
        backgroundColor: T.surfaceSoft
      },
      bar: {
        borderRadius: 9999
      }
    },

    MuiTooltip: {
      tooltip: {
        backgroundColor: T.ink,
        color: T.canvas,
        fontSize: 12,
        borderRadius: 6
      }
    }
  },

  props: {
    MuiButton: {
      disableElevation: true,
      disableRipple: true
    },
    MuiPaper: {
      elevation: 1
    },
    MuiTextField: {
      variant: 'outlined',
      size: 'small'
    }
  },

  // Exposed for components that need a raw hex — konva fills, contrast math,
  // anything that manipulates the value rather than just writing it to CSS.
  simulalert: {
    indicator: INDICATOR,
    indicatorText: INDICATOR_TEXT,
    mono: FONT_MONO
  }
});
